/**
 * SEO-URL задач: /problems/{topicSlug}/{problemSlug}
 * Логика должна совпадать с server/utils/problemSlugs.js
 */

const CYRILLIC_MAP: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh',
  з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
  п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts',
  ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

/** Тема каталога, которая в списке задач всегда последняя и не идёт в прогресс профиля */
export const SPECIAL_TOPIC = 'Специальные';

const TOPIC_SLUG_OVERRIDES: Record<string, string> = {
  SQL: 'sql',
  Algorithms: 'algorithms',
  Math: 'math',
  Physics: 'physics',
  [SPECIAL_TOPIC]: 'special',
};

const RESERVED_TOPIC_SLUGS = new Set(['suggest', 'create', 'sitemap']);

/** Сопоставление направлений главной → topic / URL-slug */
export const HOME_DIRECTION_TOPICS: Record<string, { topic?: string; topicSlug: string }> = {
  algorithms: { topic: 'Algorithms', topicSlug: 'algorithms' },
  sql: { topic: 'SQL', topicSlug: 'sql' },
  math: { topic: 'Math', topicSlug: 'math' },
  physics: { topic: 'Physics', topicSlug: 'physics' },
  ml: { topicSlug: 'machine-learning' },
  pandas: { topicSlug: 'pandas' },
};

function transliterate(input: string): string {
  let out = '';
  for (const ch of input) {
    const lower = ch.toLowerCase();
    if (CYRILLIC_MAP[lower] !== undefined) {
      out += CYRILLIC_MAP[lower];
    } else {
      out += ch;
    }
  }
  return out;
}

export function slugify(text: string): string {
  const raw = transliterate(String(text || '').trim().toLowerCase());
  const slug = raw
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
  return slug.slice(0, 180);
}

export function topicToSlug(topic: string | null | undefined): string {
  const t = String(topic || '').trim();
  if (!t) return 'misc';
  if (TOPIC_SLUG_OVERRIDES[t]) return TOPIC_SLUG_OVERRIDES[t];
  const slug = slugify(t) || 'topic';
  if (RESERVED_TOPIC_SLUGS.has(slug)) return `${slug}-topic`;
  return slug;
}

export function findTopicBySlug(
  topicSlug: string | null | undefined,
  topics: string[]
): string | null {
  const needle = String(topicSlug || '').trim().toLowerCase();
  if (!needle || !Array.isArray(topics)) return null;
  return topics.find((t) => topicToSlug(t) === needle) || null;
}

export function topicProblemsPath(topic: string | null | undefined): string {
  if (!topic || topic === 'All') return '/problems';
  return `/problems/${topicToSlug(topic)}`;
}

export type ProblemPathInput = {
  id: number;
  topic?: string | null;
  slug?: string | null;
};

export function problemPath(problem: ProblemPathInput): string {
  const tSlug = topicToSlug(problem.topic);
  const pSlug = (problem.slug && String(problem.slug).trim()) || `problem-${problem.id}`;
  return `/problems/${tSlug}/${pSlug}`;
}

export function isNumericProblemIdParam(value: string | undefined): boolean {
  return typeof value === 'string' && /^\d+$/.test(value);
}
